import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const services = [
    { 
      name: 'Belægning', 
      path: '/ydelser/belaegning',
      subServices: [
        { name: 'Ny indkørsel', path: '/ydelser/belaegning/ny-indkoersel' },
        { name: 'Ny terrasse', path: '/ydelser/belaegning/ny-terrasse' },
        { name: 'Støttemur', path: '/ydelser/belaegning/stoettemur' }
      ]
    },
    { name: 'Entreprenør', path: '/ydelser/entreprenoer' },
    { name: 'Støbearbejde', path: '/ydelser/stoebearbejde' },
    { name: 'Betonarbejde', path: '/ydelser/betonarbejde' },
    { name: 'Murerarbejde', path: '/ydelser/murerarbejde' },
    { name: 'Totalløsninger', path: '/ydelser/totalloesninger' },
    { 
      name: 'Anlægsarbejde', 
      path: '/ydelser/anlaegsarbejde',
      subServices: [
        { name: 'Haveanlæg', path: '/ydelser/anlaegsarbejde/haveanlaeg' }
      ]
    },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="relative z-50 w-full border-b border-[#E2E2E0] bg-white shadow-sm">
      {/* Top Contact Bar */}
      <div className="bg-[#1C1E1D] text-[#F4F1EA] py-2 px-4 text-xs sm:text-sm">
        <div className="mx-auto flex max-w-7xl flex-col lg:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1">
            <a href="tel:+4530713311" className="hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-phone text-white text-xs shrink-0" />
              <span className="font-medium">Kristian: <span className="whitespace-nowrap">30 71 33 11</span></span>
            </a>
            <a href="tel:+4591968419" className="hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-phone text-white text-xs shrink-0" />
              <span className="font-medium">Laurits: <span className="whitespace-nowrap">91 96 84 19</span></span>
            </a>
            <a href="mailto:kontakt@logkaps.dk" className="hover:text-white transition-colors flex items-center gap-2 whitespace-nowrap">
              <i className="fa-solid fa-envelope text-white text-xs shrink-0" />
              <span className="font-medium">Email: <span className="whitespace-nowrap">kontakt@logkaps.dk</span></span>
            </a>
          </div>
          <div className="flex flex-wrap justify-center lg:justify-end gap-x-4 gap-y-1 text-center whitespace-nowrap">
            <div className="flex items-center gap-1.5">
              <i className="fa-solid fa-location-dot text-white text-xs shrink-0" />
              <span className="font-medium">Krarupvej 58, 7800 Skive</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          
          {/* Logo / Brand */}
          <Link to="/" className="flex items-center group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=85,format=auto/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/logo-lk_2.png" 
              alt="Larsen & Kristensen" 
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-semibold tracking-wide transition-colors hover:text-[#163A2B] ${
                isActive('/') ? 'text-[#163A2B] border-b-2 border-[#163A2B] py-1' : 'text-[#1C1E1D]/80'
              }`}
            >
              Forside
            </Link>
            
            <Link 
              to="/om-larsen-kristensen" 
              className={`text-sm font-semibold tracking-wide transition-colors hover:text-[#163A2B] ${
                isActive('/om-larsen-kristensen') ? 'text-[#163A2B] border-b-2 border-[#163A2B] py-1' : 'text-[#1C1E1D]/80'
              }`}
            >
              Om os
            </Link>

            {/* Dropdown Menu */}
            <div 
              className="relative" 
              ref={dropdownRef}
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors hover:text-[#163A2B] cursor-pointer ${
                  isActive('/ydelser') ? 'text-[#163A2B] border-b-2 border-[#163A2B] py-1' : 'text-[#1C1E1D]/80'
                }`}
              >
                Ydelser
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-64 z-50">
                  <div className="rounded-md border border-[#E2E2E0] bg-white py-2 shadow-lg ring-1 ring-black/5">
                    {services.map((service) => (
                      <div key={service.path} className="group/service">
                        <Link
                          to={service.path}
                          className={`block px-4 py-2.5 text-sm transition-colors hover:bg-[#F4F1EA] ${
                            location.pathname === service.path ? 'bg-[#F4F1EA] text-[#163A2B] font-semibold' : 'text-[#1C1E1D]/90'
                          }`}
                        >
                          {service.name}
                        </Link>
                        {service.subServices && (
                          <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-300 ease-in-out group-hover/service:grid-rows-[1fr] group-hover/service:opacity-100">
                            <div className="overflow-hidden">
                              {service.subServices.map((sub) => (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className={`block pl-8 pr-4 py-1.5 text-xs transition-colors hover:bg-[#F4F1EA] ${
                                    location.pathname === sub.path ? 'text-[#163A2B] font-semibold' : 'text-[#1C1E1D]/70'
                                  }`}
                                >
                                  <i className="fa-solid fa-angle-right mr-1.5 opacity-60" /> {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/kontakt" 
              className={`text-sm font-semibold tracking-wide transition-colors hover:text-[#163A2B] ${
                isActive('/kontakt') ? 'text-[#163A2B] border-b-2 border-[#163A2B] py-1' : 'text-[#1C1E1D]/80'
              }`}
            >
              Kontakt
            </Link>

            <Link 
              to="/kontakt" 
              className="ml-4 inline-flex items-center justify-center rounded-md bg-[#163A2B] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1C1E1D] transition-all duration-200"
            >
              Få et uforpligtende tilbud
            </Link>
          </nav>

          {/* Mobile Menu Button (Visible < 1200px) */}
          <div className="flex min-[1200px]:hidden items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center rounded-md p-2 text-[#1C1E1D] hover:bg-[#F4F1EA] hover:text-[#163A2B] transition-colors focus:outline-none"
              aria-label="Hovedmenu"
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Navigation Panel (Visible < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-[#E2E2E0] bg-white shadow-inner">
          <div className="space-y-1 px-4 py-4">
            <Link
              to="/"
              className={`block rounded-md px-3 py-2.5 text-base font-semibold ${
                isActive('/') && location.pathname === '/' ? 'bg-[#F4F1EA] text-[#163A2B]' : 'text-[#1C1E1D] hover:bg-[#F4F1EA]'
              }`}
            >
              Forside
            </Link>
            
            <Link
              to="/om-larsen-kristensen"
              className={`block rounded-md px-3 py-2.5 text-base font-semibold ${
                isActive('/om-larsen-kristensen') ? 'bg-[#F4F1EA] text-[#163A2B]' : 'text-[#1C1E1D] hover:bg-[#F4F1EA]'
              }`}
            >
              Om os
            </Link>

            {/* Mobile Ydelser Section */}
            <div className="rounded-md border border-[#E2E2E0] bg-[#F4F1EA]/30 p-2 my-2">
              <div className="block px-3 py-2 text-base font-bold text-[#163A2B]">
                Ydelser
              </div>
              <div className="border-t border-[#E2E2E0] my-1.5"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-2">
                {services.map((service) => (
                  <div key={service.path} className="space-y-1">
                    <Link
                      to={service.path}
                      className={`block rounded-md px-3 py-2 text-sm ${
                        location.pathname === service.path ? 'bg-[#F4F1EA] text-[#163A2B] font-semibold' : 'text-[#1C1E1D]/80 hover:bg-[#F4F1EA]'
                      }`}
                    >
                      {service.name}
                    </Link>
                    {service.subServices && service.subServices.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`block rounded-md pl-6 pr-3 py-1.5 text-xs ${
                          location.pathname === sub.path ? 'text-[#163A2B] font-semibold' : 'text-[#1C1E1D]/70 hover:bg-[#F4F1EA]'
                        }`}
                      >
                        <i className="fa-solid fa-angle-right mr-1.5 opacity-60" /> {sub.name}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/kontakt"
              className={`block rounded-md px-3 py-2.5 text-base font-semibold ${
                isActive('/kontakt') ? 'bg-[#F4F1EA] text-[#163A2B]' : 'text-[#1C1E1D] hover:bg-[#F4F1EA]'
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-4">
              <Link
                to="/kontakt"
                className="flex w-full items-center justify-center rounded-md bg-[#163A2B] py-3 text-center text-base font-semibold text-white shadow-sm hover:bg-[#1C1E1D] transition-colors"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
